<template>
    <div v-bind:id="id" class="base-list" :class="containerClasses">
        <form-field v-if="show_searchbox">
            <input-text
                placeholder="Søg..."
                type="search"
                v-model="searchValue"
                @input="$emit('search', searchValue)"
            />
        </form-field>
        <table class="table" :class="tableClasses">
            <thead v-if="displayHeader">
                <tr>
                    <th
                        v-for="(col, index) in columnOpts"
                        :key="'th-'+index"
                        v-if="!col.hidden"
                        :width="parseWidth(col.width)"
                        @click="updateOrder(col.name)"
                    >{{ col.title }}</th>
                </tr>
            </thead>
            <tbody>
                <template v-if="useSlot">
                    <template v-for="row in rows">
                        <slot :item="row"></slot>
                    </template>
                </template>
                <template v-else>
                    <tr
                        v-for="(row, rowIndex) in rows"
                        :key="'row-' + rowIndex"
                        class="row"
                        :class="{ 'clickable': rowsClickable }"
                        @click="rowClick(row)"
                    >
                        <td
                            v-for="(col, colIndex) in columnOpts"
                            :key="'col-' + rowIndex + '-' + colIndex"
                            v-if="!col.hidden"
                            :class="col.classes(row)"
                            class="overflow-dots"
                            :title="col.hasTooltip ? (col.template ? col.template(row) : row[col.name]) : ''"
                        >
                            <template v-if="col.displayActions">
                                <div class="has-text-right">
                                    <span
                                        v-for="(action, actionIndex) in actions"
                                        :key="'action-' + rowIndex + '-' + actionIndex"
                                        class="action"
                                        aria-hidden="true"
                                        v-html="action.content"
                                        @click.stop="action.handle(row)"
                                    ></span>
                                </div>
                            </template>
                            <slot :name="'column-' + col.name" :row="row" v-else>
                                <template v-if="col.template">
                                    <span v-html="col.template(row)"></span>
                                </template>
                                <template v-else>{{ row[col.name] }}</template>
                            </slot>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <nav class="pagination is-right" :class="paginationClasses" v-if="pagination">
            <ul class="pagination-list" :class="paginationListClasses" v-if="pagination === 'default'">
                <li
                    v-for="(link, index) in defaultPagination"
                    :key="'pl-'+index"
                    :class="{ 'active': link.active }"
                >
                    <a
                        :class="['pagination-link', { 'is-current': link.active }]"
                        @click="changePage(link)"
                    >{{ link.title || '...' }}</a>
                </li>
            </ul>
            <ul class="base-list-pagination-list" :class="paginationListClasses" v-if="pagination === 'minimum'">
                <li
                    v-for="(link, index) in minimumPagination"
                    :key="'pl-'+index"
                    :class="{ 'active': link.active, 'is-current': link.active }"
                >
                    <a
                        :class="['pagination-link', { 'is-current': link.active }]"
                        @click="changePage(link)"
                    >{{ link.title || '...' }}</a>
                </li>
            </ul>
        </nav>
    </div>
</template>


<script>
import { InputText, FormField } from '@elems/form'

export default {
    components: {
        InputText,
        FormField
    },

    name: 'list',

    props: {
        data: { type: Array, required: true },

        // [{ name: String, title: String, sortable: Bool, searchable: Bool, hidden: Bool, width: Number, classes: function(row) }]
        columns: Array,

        // 0 = ∞
        pageLength: { type: Number, default: 10 },

        // false, 'default', 'minimum'
        pagination: { type: [Boolean, String], default: 'default' },

        // ID of container div
        id: String,

        // Search string
        search: String,

        // Threshold for showing the inlist search box. Will only be enabled if there are columns which are searchable.
        // Set to 0 to disable the searchbox
        enableSearchBoxRowsThreshold: {type: Number, default: 8},

        // Can rows be clicked?
        rowsClickable: { type: Boolean, default: true },

        // Styles/classes
        containerClasses: { type: [String, Array], default: 'base-list-default' },
        tableClasses: [String, Array],
        paginationClasses: [String, Array],
        paginationListClasses: [String, Array],
        paginationLinkClasses: [String, Array],

        // { col: 'name', dir: 'asc' }
        defaultOrder: Object,

        // 1 -> ?
        defaultPage: { type: Number, validator: (v) => v > 0 },

        // Should match pattern in defaultTranslations()
        translations: Object,

        // Actions displayed in 'actions' column
        actions: { type: Array, default: () => [] },

        // Should headers be showed?
        displayHeader: { type: Boolean, default: true },

        scrollToTop: { type: Boolean, default: true },
    },

    data() {
        return {
            state: {
                order: { col: '', dir: '' },
                page: 1,
            },
            searchValue: ''
        }
    },

    watch: {
        defaultOrder() {
            this.setDefaultState({ preservePage: true })
        },
        search(value) {
            this.searchValue = value
        }
    },

    computed: {
        show_searchbox() {
            return (
                this.enableSearchBoxRowsThreshold !== 0
                && this.enableSearchBoxRowsThreshold < this.data.length
                && this.columns.some(column => column.searchable)
            );
        },

        rows() {
            if(this.pageLength > 0 && this.filteredRows.length > this.pageLength) { // Paginate
                return this.orderedRows.slice((this.state.page-1) * this.pageLength, ((this.state.page-1) * this.pageLength) + this.pageLength)
            }
            return this.orderedRows
        },

        orderedRows() {
            const colConfig = this.columnOpts.find(col => col.name === this.state.order.col)
            const toSortable = (obj, key) => {
                const value = typeof colConfig.sort === 'function' ? colConfig.sort(obj) : obj[key]
                return typeof value === 'string' ? value.toLowerCase() : value
            }

            const rows = [].concat(this.filteredRows)
            return rows.sort((a, b) => {
                const valA = toSortable(a, this.state.order.col)
                const valB = toSortable(b, this.state.order.col)
                if(this.state.order.dir === 'asc') {
                    return (valA < valB ? -1 : (valA > valB ? 1 : 0))
                } else {
                    return (valA > valB ? -1 : (valA < valB ? 1 : 0))
                }
            })
        },

        filteredRows() {
            if(this.searchValue) {
                return this.data.filter(row => {
                    for(let key in row) {
                        if(row[key] == null) {
                            continue
                        }

                        const col = this.columnOpts.filter(c => c.name === key)

                        if(!col.length || !col[0].searchable) {
                            continue
                        }

                        const value = typeof col[0].search === 'function' ? col[0].search(row) : row[key]

                        if(value.toString().toLowerCase().indexOf(this.query) >= 0) {
                            return true
                        }
                    }
                    return false
                })
            }
            return this.data
        },

        query() {
            return this.searchValue.toLowerCase()
        },

        columnOpts() {
            let cols = []
            if(this.columns) {
                cols = this.columns
            } else if(this.data.length) {
                for(let key in this.data[0]) {
                    cols.push({ name: key })
                }
            }

            const actionColIndex = cols.findIndex(item => item.displayActions === true)

            if(this.actions.length && actionColIndex === -1) {
                cols.push({
                    name: 'actions',
                    title: '',
                    displayActions: true,
                })
            }

            const restWidth = cols.reduce((sum, col) => { return sum - (col.width || 0) }, 100)
            const remainingCols = cols.filter(col => col.width === undefined).length
            const defaultColWidth = Math.floor(restWidth / remainingCols)

            // Make sure columns have all options set
            return cols.map(col => {
                if(typeof col !== 'object') {
                    col = { name: col.toString() }
                }
                col.title       = typeof col.title === 'undefined' ? col.name : col.title
                col.searchable  = typeof col.searchable !== 'boolean' ? true : col.searchable
                col.sortable    = typeof col.sortable !== 'boolean' ? true : col.sortable
                col.hidden      = typeof col.hidden !== 'boolean' ? false : col.hidden
                col.classes     = typeof col.classes !== 'function' ? (row) => [] : col.classes
                col.width       = typeof col.width === 'undefined' ? defaultColWidth : col.width
                return col
            })
        },

        defaultPagination() {
            const curPage = this.state.page,
                maxPage = Math.ceil(this.filteredRows.length / this.pageLength),
                delta = 2,
                left = curPage - delta,
                right = curPage + delta + 1,
                range = [],
                rangeWithDots = []

            for (let i = 1; i <= maxPage; i++) {
                if (i == 1 || i == maxPage || i >= left && i < right) {
                    range.push(i)
                }
            }

            let l = null
            range.forEach(i => {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(this.createPaginationLink(l + 1))
                    } else if (i - l !== 1) {
                        rangeWithDots.push(this.createPaginationLink(null))
                    }
                }
                rangeWithDots.push(this.createPaginationLink(i))
                l = i
            })

            return rangeWithDots
        },

        minimumPagination() {
            return [
                this.createPaginationLink({ page: null, title: this.translate('pagination.prev'), prev: true }),
                this.createPaginationLink({ page: null, title: this.translate('pagination.next'), next: true }),
            ]
        },

        defaultTranslations() {
            return {
                pagination: {
                    next: 'Next',
                    prev: 'Previous',
                }
            }
        },

        useSlot() {
            return !!this.$scopedSlots.default || !!this.$slots.default
        },
    },

    created() {
        this.setDefaultState()
        this.searchValue = this.search
    },

    methods:{

        setDefaultState(preservePage = false) {
            const state = this.getDefaultState()
            if(this.defaultOrder) {
                state.order.col = this.defaultOrder.col
                state.order.dir = this.defaultOrder.dir ? this.defaultOrder.dir : 'asc'
            } else {
                state.order.col = this.columnOpts[0].name
                state.order.dir = 'asc'
            }
            if (preservePage) {
                state.page = this.state.page
            } else if(this.defaultPage) {
                state.page = this.defaultPage
            }
            this.$set(this, 'state', state)
        },

        getDefaultState() {
            return {
                order: { col: '', dir: '' },
                page: 1,
            }
        },

        updateOrder(col) {
            if(this.state.order.col === col) {
                this.state.order.dir = this.state.order.dir === 'asc' ? 'desc' : 'asc'
            } else {
                this.state.order = { col: col, dir: 'asc' }
            }

            this.$emit('orderUpdated', this.state.order)
        },

        rowClick(row) {
            if(this.rowsClickable) {
                this.$emit('rowClicked', row)
            }
        },

        changePage(link) {
            if(typeof link === 'object') {
                if(link.page) {
                    this.state.page = link.page
                } else if(link.next !== undefined && link.next) {
                    this.state.page++
                } else if(link.prev !== undefined && link.prev) {
                    this.state.page--
                }
            } else {
                this.state.page = link
            }

            if (this.scrollToTop) {
                window.scrollTo(0, 0);
            }
            
            this.$emit('pageChanged', this.state.page)
        },

        createPaginationLink(page = {}) {
            const link = { page: null }
            if(typeof page !== 'object') {
                link.page = page
            } else {
                for(let key in page) {
                    link[key] = page[key]
                }
            }
            link.title = link.title === undefined ? link.page : link.title
            link.active = (link.page === this.state.page)
            return link
        },

        translate(key) {
            let translation = null
            if(this.translations) {
                if(translation = key.split('.').reduce((o,i)=>o[i], this.translations)) {
                    return translation
                }
            }

            if(translation = key.split('.').reduce((o,i)=>o[i], this.defaultTranslations)) {
                return translation
            }

            return key;
        },

        parseWidth(width) {
            if (parseInt(width) == width) {
                return width + '%'
            }

            return width || ''
        }

    }
}
</script>

<style scoped>
.base-list table {
    table-layout: fixed;
}
.base-list table thead th {
    cursor: pointer;
}
.base-list table tr.clickable {
    cursor: pointer;
}
.base-list table td .action {
    margin-left: 5px;
}
.min-width{
    min-width: 1000px;
}
.overflow-dots {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
